import { Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { UserButton } from '@clerk/clerk-react';
import { LayoutDashboard, BookOpen, Timer, PlusCircle } from 'lucide-react';

import Homepage from './Homepage';
import logoMono from '../../assets/logo-mono.png';

// ----------------------------------------------------------------
// Coming Soon placeholder — used until features are built
// ----------------------------------------------------------------
function ComingSoon({ feature }: { feature: string }) {
  return (
    <div
      className="flex flex-col items-center justify-center"
      style={{ minHeight: '60vh' }}
    >
      <div className="panel p-10 text-center max-w-md w-full">
        <span className="label-tag mb-4 inline-block">{feature}</span>
        <h2
          className="text-2xl mb-3"
          style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-text)' }}
        >
          Coming Soon
        </h2>
        <p
          className="text-sm mb-8"
          style={{ color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}
        >
          This feature is in development and will be available in an upcoming release.
        </p>
        <Link to="/app" className="btn-ghost text-xs">
          ← Back to Dashboard
        </Link>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------
// Nav link items
// ----------------------------------------------------------------
const NAV_LINKS = [
  { to: '/app', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { to: '/app/skills', label: 'Skills', icon: BookOpen, exact: false },
  { to: '/app/timer', label: 'Timer', icon: Timer, exact: false },
];

// ----------------------------------------------------------------
// AppLayout — wraps all /app/* routes
// ----------------------------------------------------------------
export default function AppLayout() {
  const location = useLocation();

  function isActive(to: string, exact: boolean) {
    return exact
      ? location.pathname === to
      : location.pathname.startsWith(to);
  }

  return (
    <div
      className="min-h-screen grid-paper"
      style={{ background: 'var(--color-bg)' }}
    >
      {/* ── Top Navigation Bar ── */}
      <nav
        className="sticky top-0 z-40"
        style={{
          background: 'var(--color-surface)',
          borderBottom: '1px solid var(--color-border)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">

            {/* Left: logo + nav links */}
            <div className="flex items-center gap-6">
              <Link to="/" className="flex items-center gap-2 shrink-0">
                <img
                  src={logoMono}
                  alt="OwensRoot"
                  className="h-6 w-auto"
                  style={{ opacity: 0.85 }}
                />
              </Link>

              <div className="hidden sm:flex items-center gap-1">
                {NAV_LINKS.map(({ to, label, icon: Icon, exact }) => (
                  <Link
                    key={to}
                    to={to}
                    className="flex items-center gap-1.5 px-3 py-1.5 transition-all"
                    style={{
                      fontFamily: 'var(--font-label)',
                      fontSize: '0.65rem',
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                      color: isActive(to, exact) ? 'var(--color-accent)' : 'var(--color-text-muted)',
                      background: isActive(to, exact) ? 'var(--color-accent-dim)' : 'transparent',
                      border: isActive(to, exact)
                        ? '1px solid var(--color-border-hi)'
                        : '1px solid transparent',
                      borderRadius: '2px',
                    }}
                  >
                    <Icon size={12} />
                    {label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Right: new skill + user */}
            <div className="flex items-center gap-3">
              <Link
                to="/app/skills/new"
                className="hidden sm:inline-flex items-center gap-1.5 btn-ghost py-1.5 px-3 text-xs"
              >
                <PlusCircle size={13} />
                New Skill
              </Link>
              <UserButton afterSignOutUrl="/" />
            </div>

          </div>
        </div>
      </nav>

      {/* ── Main content ── */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Routes>
          <Route index element={<Homepage />} />
          <Route path="skills" element={<ComingSoon feature="Skills Tracker" />} />
          <Route path="skills/new" element={<ComingSoon feature="New Skill" />} />
          <Route path="timer" element={<ComingSoon feature="Focus Timer" />} />
          <Route path="*" element={<Navigate to="/app" replace />} />
        </Routes>
      </main>
    </div>
  );
}
